import React, { useState, useEffect, useRef } from 'react';
import {
  Drawer,
  List,
  Box,
  IconButton,
  AppBar,
  Toolbar,
  Typography,
  Tooltip,
  Menu,
  MenuItem as MuiMenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  MenuOutlined as MenuIcon,
  CloseOutlined as CloseIcon,
  HomeOutlined as HomeIcon,
  BusinessOutlined as BusinessIcon,
  ArrowBackOutlined as ArrowBackIcon,
  SpaceDashboardOutlined as CompanyDashboardIcon,
  WarningAmberOutlined as IncidentIcon,
  GavelOutlined as AttentionIcon,
  HelpOutlineOutlined as HelpIcon,
  ChatBubbleOutlineOutlined as MessageIcon,
  NotificationsNoneOutlined as NotificationsIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from 'shared/contexts/AuthContext';
import { UserRole } from 'shared/types';
import { subscriptionApi } from 'shared/services/planApi';
import { useCompany } from 'shared/hooks/queries/useCompanies';
import { useCompanyLogoUrl } from 'shared/hooks/queries/useCompanyTheme';
import { MenuItem, useMenuItems, getCategorizedMenuItems } from './menuConfig';
import { SidebarMenuItem } from './SidebarMenuItem';
import { QuickLinksSidebarItem } from './QuickLinksSidebarItem';
import { PLATFORM_ROLES } from 'shared/utils/roleUtils';

interface SidebarProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onOpenProfile?: () => void;
  profileOpen?: boolean;
  onCloseProfile?: () => void;
  onOpenIncident?: () => void;
  onOpenAttentionCall?: () => void;
  onOpenSupportTicket?: () => void;
}

const DRAWER_WIDTH = 260;
const COLLAPSED_WIDTH = 76;
const TOP_APP_BAR_HEIGHT = 64;

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed = false,
  onToggleCollapse,
  onOpenProfile,
  profileOpen = false,
  onCloseProfile,
  onOpenIncident,
  onOpenAttentionCall,
  onOpenSupportTicket,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const navigate = useNavigate();
  const location = useLocation();
  const { user, hasRole, userAvatarUrl } = useAuth();
  
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [reportMenuAnchor, setReportMenuAnchor] = useState<null | HTMLElement>(null);
  const menuItems = useMenuItems();

  // Get the user's company info (moved up for use in categorizedMenuItems)
  const userCompanyId = user?.company?._id;
  const userCompanyName = user?.company?.name;
  // Get categorized menu items for better organization
  const rawCategorizedMenuItems = user?.role
    ? getCategorizedMenuItems(user.role, hasRole, user)
    : [];

  // For approvers, replace "Mi Empresa" with the actual company name
  // For company_admin, replace the category label "Mi Empresa" with the company name
  const isUserCompanyAdmin = user?.role === UserRole.COMPANY_ADMIN;
  const categorizedMenuItems = rawCategorizedMenuItems.map(category => {
    if (category.category === 'Aprobador' && userCompanyName) {
      return {
        ...category,
        items: category.items.map(item => {
          if (item.id === 'mi-empresa-approver') {
            return { ...item, title: userCompanyName };
          }
          return item;
        })
      };
    }
    // For company_admin, show company name as category label instead of "Mi Empresa"
    if (category.category === 'Mi Empresa' && isUserCompanyAdmin && userCompanyName) {
      return {
        ...category,
        category: userCompanyName
      };
    }
    return category;
  });

  const companyMatch = location.pathname.match(/^\/companies\/([^/]+)/);
  const isCompanyDetailPage = 
    companyMatch && 
    !location.pathname.includes('/companies/new') && 
    !location.pathname.includes('/companies/unlinked-users');
  const companyId = isCompanyDetailPage && companyMatch ? companyMatch[1] : null;

  const isPlatformUser = hasRole([UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF]);
  const isViewingCompany = !!(companyId && companyId !== 'unlinked-users');

  // Shared React Query cache for the scoped company — multiple consumers
  // (Layout, ModuleGuard, page-level components) hit the same cache entry
  // so the `/companies/:id` GET fires once per 5 minutes regardless of how
  // many components mount.
  const isLoadingCompanyId = !!(companyId && companyId !== 'unlinked-users');
  const companyQuery = useCompany(companyId || '', isLoadingCompanyId);
  const company = isLoadingCompanyId ? (companyQuery.data?.data ?? null) : null;

  // Load company logo from theme settings via shared React Query cache so
  // theme + presigned-logo fetches are deduped across Sidebar/CompanyDetail
  // and not re-fired on every route change.
  const logoCompanyId =
    companyId && companyId !== 'unlinked-users'
      ? companyId
      : userCompanyId || null;
  const companyLogoQuery = useCompanyLogoUrl(logoCompanyId);
  const companyLogoUrl = companyLogoQuery.data ?? null;
  const mainLogoUrl = isPlatformUser ? '/safety-logo.png' : (companyLogoUrl || '/safety-logo.png');

  useEffect(() => {
    if (location.pathname.includes('/work-permits') || location.pathname.includes('/service-cards') || location.pathname.includes('/evaluations') || location.pathname.includes('/permits') || location.pathname.includes('/library')) {
      setExpandedItems(prev =>
        prev.includes('Permisos de Trabajo') ? prev : [...prev, 'Permisos de Trabajo']
      );
    } else {
      setExpandedItems(prev => prev.filter(item => item !== 'Permisos de Trabajo'));
    }
  }, [location.pathname]);

  // Filter menu items by enabled modules (uses viewed company for admins, user's company for others)
  const activeCompanySettings = (isViewingCompany && company)
    ? (company as any)?.settings
    : user?.company?.settings;

  // Plan-feature flags for the *user's own* company (not the company they're
  // viewing as super_admin). Used to gate items like Marketplace that are
  // tied to the subscription tier rather than per-company settings.
  const [planFeatures, setPlanFeatures] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (!userCompanyId || isPlatformUser) {
      // Platform users bypass plan gating — they don't have a subscription.
      setPlanFeatures({ includesMarketplace: true });
      return;
    }
    let cancelled = false;
    subscriptionApi
      .getByCompany(userCompanyId)
      .then((sub: any) => {
        if (cancelled) return;
        const f = sub?.plan && typeof sub.plan === 'object' ? sub.plan.features : null;
        setPlanFeatures(f || {});
      })
      .catch(() => { if (!cancelled) setPlanFeatures({}); });
    return () => { cancelled = true; };
  }, [userCompanyId, isPlatformUser]);

  // Platform-level module toggles. When marketplace/workPermits is off here
  // the entry is hidden for everyone, including super_admins.
  const [platformModules, setPlatformModules] = useState<Record<string, boolean> | null>(null);
  useEffect(() => {
    let cancelled = false;
    import('shared/services/platformSettingsApi').then(({ platformSettingsApi }) => {
      platformSettingsApi.get()
        .then((s) => { if (!cancelled) setPlatformModules(s.modules as unknown as Record<string, boolean>); })
        .catch(() => { if (!cancelled) setPlatformModules(null); });
    });
    return () => { cancelled = true; };
  }, []);

  const filteredCategorizedMenuItems = categorizedMenuItems.map(category => ({
    ...category,
    items: category.items.filter(item => {
      if (item.requiresModule) {
        // Platform-level kill switch wins over per-company configuration.
        if (platformModules && platformModules[item.requiresModule] === false) {
          return false;
        }
        if (activeCompanySettings?.modules &&
            activeCompanySettings.modules[item.requiresModule] === false) {
          return false;
        }
      }
      if (item.requiresPlanFeature) {
        if (!planFeatures[item.requiresPlanFeature]) return false;
      }
      return true;
    }),
  })).filter(category => category.items.length > 0);

  /**
   * Menú por contexto. Antes empezaba con una rama de ~80 líneas para
   * plataforma dentro de una empresa (`companyId && hasRole([SUPER_ADMIN,
   * SAFETY_STAFF])`), pero el render de abajo sólo usa esta lista cuando
   * `!hasRole([...PLATFORM_ROLES])` — condiciones mutuamente excluyentes, así
   * que esa rama nunca pudo pintarse. Los roles de plataforma conservan su
   * sidebar plano a propósito (ver el comentario del render); se eliminó el
   * bloque muerto en lugar de activarlo.
   */
  const getContextualMenuItems = (): MenuItem[] =>
    menuItems.filter(item => item.roles.some(role => hasRole(role)));

  const filteredMenuItems = getContextualMenuItems().filter(item => {
    if (!item.requiresModule) return true;
    if (!activeCompanySettings?.modules) return true;
    return activeCompanySettings.modules[item.requiresModule] !== false;
  });

  // Leave-company confirmation: when in company context and clicking the global Inicio,
  // ask the user to confirm leaving the company shell. They can opt out with a checkbox
  // (persisted in localStorage).
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [pendingNavPath, setPendingNavPath] = useState<string | null>(null);
  const [skipLeaveConfirm, setSkipLeaveConfirm] = useState(false);

  const performNavigate = (path: string) => {
    navigate(path);
    if (isMobile) setMobileMenuOpen(false);
  };

  const handleNavigate = (path: string) => {
    const optedOut = (() => {
      try { return localStorage.getItem('inicio.skipLeaveConfirm') === '1'; }
      catch { return false; }
    })();
    const shouldConfirm = inCompanyContext && path === '/inicio' && !optedOut;
    if (shouldConfirm) {
      setPendingNavPath(path);
      setSkipLeaveConfirm(false);
      setLeaveDialogOpen(true);
      return;
    }
    performNavigate(path);
  };

  // In-app visited-page stack for the "Volver" button. Sidebar (and Layout)
  // stay mounted for the whole authenticated session, so this ref survives
  // across nested route navigation and resets naturally on logout/re-login.
  // Each click pops one page off the stack; once it's exhausted, further
  // clicks land on (and stay on) /inicio — never on raw browser history,
  // which used to let repeated clicks walk past login.
  const backHistoryRef = useRef<string[]>([]);

  useEffect(() => {
    const current = location.pathname + location.search;
    const stack = backHistoryRef.current;
    if (stack[stack.length - 1] !== current) {
      stack.push(current);
      if (stack.length > 50) stack.shift();
    }
  }, [location.pathname, location.search]);

  const handleGoBack = () => {
    const stack = backHistoryRef.current;
    if (stack.length > 0) stack.pop();
    const previous = stack.length > 0 ? stack[stack.length - 1] : null;
    // Bypass handleNavigate's "leave company context" confirmation — that
    // dialog is for the explicit Inicio button, not for stepping back.
    performNavigate(previous || '/inicio');
  };

  const handleConfirmLeave = () => {
    if (skipLeaveConfirm) {
      try { localStorage.setItem('inicio.skipLeaveConfirm', '1'); }
      catch { /* noop */ }
    }
    setLeaveDialogOpen(false);
    if (pendingNavPath) performNavigate(pendingNavPath);
    setPendingNavPath(null);
  };

  const handleCancelLeave = () => {
    setLeaveDialogOpen(false);
    setPendingNavPath(null);
  };

  const toggleExpanded = (itemTitle: string) => {
    setExpandedItems(prev =>
      prev.includes(itemTitle)
        ? prev.filter(item => item !== itemTitle)
        : [...prev, itemTitle]
    );
  };

  /**
   * SidebarContent - Reusable navigation list for both desktop and mobile
   * DRY principle: Single source of truth for the navigation UI
   */
  // Flatten all categorized items into a single list (no category headers)
  const rawFlatMenuItems = filteredCategorizedMenuItems.flatMap(cat => cat.items);

  /**
   * Context-aware path rewriting for the platform 8-item sidebar.
   *
   * When platform users (super_admin / safety_staff) drill into a specific
   * company (`/companies/:companyId/...`), most sidebar items should jump to
   * the company-scoped variant of that section instead of the global one so
   * the company shell is preserved. Inicio is the explicit "exit" back to
   * the global home.
   */
  const SCOPED_MENU_PATHS: Record<string, string> = {
    // Inicio is deliberately NOT rewritten here — it always points at the
    // real global home (/inicio), even inside a company shell. Clicking it
    // is the explicit "exit company context" action (see handleNavigate's
    // confirmation dialog below). The "Dashboard" item rendered above it
    // is the in-context shortcut to the company's own resumen page.
    'sa-resumen': '/resumen',
    // Marketplace inside a company shell renders the same browser, but
    // the URL keeps the company id so the sidebar's Dashboard shortcut
    // stays visible and the "Publicar trabajo" action auto-uses this
    // company instead of prompting for one.
    'sa-marketplace': '/marketplace',
    // Mensajes — same story as marketplace: clicking it from inside a
    // company shell scopes the URL to that company so the sidebar
    // context badge + Dashboard shortcut stay visible.
    'mensajes': '/mensajes',
    'sa-en-proceso': '/en-proceso',
    // Verificaciones: global cross-company dashboard outside a company
    // shell, scoped to that one company's verifications inside it — same
    // pattern as sa-invitaciones below.
    'sa-verificaciones': '/verifications',
    'sa-solicitudes-remocion': '/solicitudes-remocion',
    // sa-historial entry was retired — Historial now lives inside the
    // Configuración page (see CompanySettings.tsx / ConfiguracionPage.tsx).
    'sa-capacitaciones': '/capacitaciones',
    'sa-configuracion': '/configuracion',
    'sa-usuarios': '/users',
    // Invitaciones: global cross-company view outside a company shell,
    // scoped to that one company's pending invitations inside it.
    'sa-invitaciones': '/invitaciones',
    // Ayuda used to drop the user back to the global shell. Keep them in
    // company context — the help-center renders identically either way.
    'sa-ayuda': '/ayuda',
  };
  // Items hidden from the sidebar while inside a company context (only).
  // `sa-notificaciones` is fully retired (top-bar bell covers it), so it's
  // already hidden by an empty roles array in menuConfig; the entry here
  // is harmless and left for back-compat with any stray persisted state.
  const HIDDEN_IN_COMPANY_CONTEXT = new Set<string>(['sa-notificaciones', 'sa-todos-los-usuarios']);
  // Items only meaningful inside a company context — drop them otherwise.
  // Usuarios shows internos/externos per-company, so the top-level page
  // has nothing useful to render globally.
  const HIDDEN_OUTSIDE_COMPANY_CONTEXT = new Set<string>(['sa-usuarios']);
  const inCompanyContext = !!(companyId && companyId !== 'unlinked-users') && hasRole([...PLATFORM_ROLES]);
  const flatMenuItems: MenuItem[] = inCompanyContext
    ? rawFlatMenuItems
        .filter((it) => !HIDDEN_IN_COMPANY_CONTEXT.has(it.id))
        .map((it) => {
          const suffix = SCOPED_MENU_PATHS[it.id];
          if (!suffix) return it;
          const scopedPath = `/companies/${companyId}${suffix}`;
          // Also scope children paths: a child whose path starts with the
          // parent suffix gets rewritten with the same companyId prefix.
          // This is what lets Usuarios > Internos / Externos work when a
          // platform user drills into /companies/:id/*.
          const scopedChildren = it.children?.map((child) => {
            if (!child.path) return child;
            const queryIdx = child.path.indexOf('?');
            const childBase = queryIdx >= 0 ? child.path.slice(0, queryIdx) : child.path;
            const childQuery = queryIdx >= 0 ? child.path.slice(queryIdx) : '';
            if (childBase === suffix) {
              return { ...child, path: `/companies/${companyId}${suffix}${childQuery}` };
            }
            return child;
          });
          return { ...it, path: scopedPath, ...(scopedChildren ? { children: scopedChildren } : {}) };
        })
    : rawFlatMenuItems.filter((it) => !HIDDEN_OUTSIDE_COMPANY_CONTEXT.has(it.id));

  // Pulled out of flatMenuItems so it can render above the company
  // "Dashboard" shortcut instead of at its normal flat-list position — see
  // the render site below, which also excludes it from flatMenuItems.map()
  // to avoid a duplicate.
  const inCompanyContextSaInicioItem = inCompanyContext
    ? flatMenuItems.find((it) => it.id === 'sa-inicio')
    : undefined;

  // The header bar (Mensajes shortcut + notification bell) is desktop-only —
  // it isn't rendered on mobile (see Layout.tsx). Surface those two
  // destinations in the mobile drawer menu instead. Deduped against the
  // role's own menu so roles that already list Notificaciones/Mensajes don't
  // get a second entry.
  const renderedListForDedup = companyId && companyId !== 'unlinked-users' && !hasRole([...PLATFORM_ROLES])
    ? filteredMenuItems
    : flatMenuItems;
  const renderedPaths = new Set(
    renderedListForDedup.map((i) => (i.path || '').split('?')[0])
  );
  const mobileExtraItems: MenuItem[] = [
    { id: 'mobile-mensajes', title: 'Mensajes', icon: <MessageIcon />, path: '/mensajes', roles: [] },
    { id: 'mobile-notificaciones', title: 'Notificaciones', icon: <NotificationsIcon />, path: '/notificaciones', roles: [] },
  ].filter((it) => !renderedPaths.has(it.path));

  const SidebarContent = (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    }}>
      {/* Logo + hamburger header. The hamburger toggles sidebar collapse
          (Reddit-style — pinned at the top instead of the old bottom button). */}
      {!isMobile && (
        <Box sx={{
          pt: collapsed ? 1.5 : 2,
          pb: collapsed ? 1 : 1.5,
          px: collapsed ? 0.5 : 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          gap: 1,
          transition: 'padding 0.3s ease',
          overflow: 'hidden',
          flexShrink: 0,
        }}>
          {!collapsed && (() => {
            // Logo click target: inside a company shell, route to that
            // company's resumen so platform users don't accidentally
            // leave the company context. Otherwise, send them home.
            const logoHref = inCompanyContext
              ? `/companies/${companyId}/resumen`
              : '/';
            return (
              <a href={logoHref} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <img
                  src={mainLogoUrl}
                  alt={mainLogoUrl === '/safety-logo.png' ? "Safety Logo" : "Company Logo"}
                  style={{
                    height: '52px',
                    width: 'auto',
                    maxWidth: '140px',
                    objectFit: 'contain',
                  }}
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = '/safety-logo.png';
                  }}
                />
              </a>
            );
          })()}
          {onToggleCollapse && (
            <Tooltip title={collapsed ? 'Expandir menú' : 'Colapsar menú'} placement="right" arrow>
              <IconButton
                onClick={onToggleCollapse}
                size="small"
                aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
                sx={{
                  color: (theme) => theme.palette.background.sidebartext,
                  opacity: 0.85,
                  '&:hover': {
                    opacity: 1,
                    bgcolor: (theme) => `${theme.palette.background.sidebartext}15`,
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
            </Tooltip>
          )}
          {collapsed && !onToggleCollapse && (
            // Fallback: tiny logo when collapse is unavailable (forced by viewport width)
            <Box
              component="a"
              href={inCompanyContext ? `/companies/${companyId}/resumen` : '/'}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              <img
                src={mainLogoUrl}
                alt="Logo"
                style={{ height: 36, width: 36, objectFit: 'cover', borderRadius: 10 }}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/safety-logo.png';
                }}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Company Identity Box - platform roles viewing a company */}
      {isPlatformUser && isViewingCompany && (
        <Box sx={{
          mx: collapsed ? 0.5 : 1.5,
          mb: 1,
          p: collapsed ? 1 : 1.5,
          borderRadius: 2,
          bgcolor: (theme) => `${theme.palette.background.sidebartext}15`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          overflow: 'hidden',
          transition: 'all 0.3s ease',
          minHeight: collapsed ? 44 : 'auto',
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}>
          {companyLogoUrl ? (
            <Box
              component="img"
              src={companyLogoUrl}
              alt={company?.name || 'Company'}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                objectFit: 'cover',
                flexShrink: 0,
              }}
              onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <Box sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (theme) => `${theme.palette.background.sidebartext}20`,
              flexShrink: 0,
            }}>
              <BusinessIcon sx={{
                fontSize: 20,
                color: (theme) => theme.palette.background.sidebartext,
              }} />
            </Box>
          )}
          {!collapsed && (
            <Typography
              variant="body2"
              noWrap
              sx={{
                color: (theme) => theme.palette.background.sidebartext,
                fontWeight: 600,
                fontSize: '0.8rem',
                lineHeight: 1.2,
                opacity: 0.9,
              }}
            >
              {company?.name || 'Cargando...'}
            </Typography>
          )}
        </Box>
      )}

      {/* Navigation Items — Home + flat list, no category headers */}
      <Box sx={{
        flexGrow: 1,
        overflow: 'auto',
        px: 0,
        pt: 0,
        pb: 1,
        transition: 'padding 0.3s ease',
        '&::-webkit-scrollbar': {
          width: 4,
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: 'rgba(0,0,0,0.1)',
          borderRadius: 2,
        },
      }}>
        <List sx={{ py: 0 }}>
          {/* Back button — goes to previous page */}
          <SidebarMenuItem
            item={{
              id: 'go-back',
              title: 'Volver',
              icon: <ArrowBackIcon />,
              path: '__BACK__',
              roles: [],
            }}
            isExpanded={false}
            onToggle={() => {}}
            onNavigate={(p: string) => {
              if (p === '__BACK__') {
                handleGoBack();
              } else {
                handleNavigate(p);
              }
            }}
            hasRole={() => true}
            collapsed={collapsed && !isMobile}
          />

          {/* Quick Links — per-user favoritos popover. Hidden for
              client_staff (Colaborador) — too few destinations to be useful. */}
          {user?.role !== UserRole.CLIENT_STAFF && (
            <QuickLinksSidebarItem collapsed={collapsed && !isMobile} />
          )}

          {/* Inicio is pulled out of flatMenuItems and rendered here (above
              Dashboard) so the rail reads top-to-bottom as "Volver /
              Favoritos / Inicio / Dashboard". It's excluded from the
              flatMenuItems.map() below to avoid rendering it twice. */}
          {inCompanyContextSaInicioItem && (
            <SidebarMenuItem
              item={inCompanyContextSaInicioItem}
              isExpanded={false}
              onToggle={() => {}}
              onNavigate={handleNavigate}
              hasRole={() => true}
              collapsed={collapsed && !isMobile}
            />
          )}

          {/* "Dashboard" — in-context shortcut back to this company's own
              resumen page, for platform users (super_admin / safety_staff)
              who navigated elsewhere within the company shell. Hidden when
              not scoped to a company. Unlike Inicio, this never leaves
              company context, so it navigates directly without the
              exit-confirmation dialog. */}
          {inCompanyContext && (
            <SidebarMenuItem
              item={{
                id: 'company-dashboard',
                title: 'Dashboard',
                icon: <CompanyDashboardIcon />,
                path: `/companies/${companyId}/resumen`,
                roles: [],
              }}
              isExpanded={false}
              onToggle={() => {}}
              onNavigate={() => performNavigate(`/companies/${companyId}/resumen`)}
              hasRole={() => true}
              collapsed={collapsed && !isMobile}
            />
          )}

          {/* Home item — hidden for roles that already have an Inicio entry in their menu. */}
          {!hasRole([UserRole.CLIENT_APPROVER, UserRole.COMPANY_ADMIN, UserRole.CLIENT_STAFF, UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF, UserRole.SECURITY]) && (
            <SidebarMenuItem
              item={{
                id: 'home',
                title: 'Inicio',
                icon: <HomeIcon />,
                path: companyId && companyId !== 'unlinked-users'
                  ? `/companies/${companyId}`
                  : hasRole([UserRole.SUPER_ADMIN, UserRole.SAFETY_STAFF])
                    ? '/dashboard'
                    : '/',
                roles: [],
                stringKey: 'inicio',
              }}
              isExpanded={false}
              onToggle={() => {}}
              onNavigate={handleNavigate}
              hasRole={() => true}
              collapsed={collapsed && !isMobile}
            />
          )}

          {/* Mobile-only: Mensajes + Notificaciones (desktop has these in the
              top header bar, which mobile hides). */}
          {isMobile && mobileExtraItems.map((item) => (
            <SidebarMenuItem
              key={item.id}
              item={item}
              isExpanded={false}
              onToggle={() => {}}
              onNavigate={handleNavigate}
              hasRole={() => true}
              collapsed={false}
            />
          ))}

          {/*
            Platform roles (super_admin / safety_staff) keep their flat 8-item
            sidebar even when drilling into company context — the contextual
            menu was a holdover from the old shell. With parentRoutes, the
            relevant top-level item (e.g. Inicio for /companies/:id) stays
            highlighted, so users still know where they are.

            Other roles continue to use the contextual menu when applicable.
          */}
          {companyId && companyId !== 'unlinked-users' && !hasRole([...PLATFORM_ROLES]) ? (
            filteredMenuItems.map((item) => (
              <SidebarMenuItem
                key={item.id}
                item={item}
                isExpanded={expandedItems.includes(item.id)}
                onToggle={toggleExpanded}
                onNavigate={handleNavigate}
                hasRole={hasRole}
                collapsed={collapsed && !isMobile}
              />
            ))
          ) : (
            flatMenuItems
              .filter((item) => !(inCompanyContextSaInicioItem && item.id === 'sa-inicio'))
              .map((item) => (
              <SidebarMenuItem
                key={item.id}
                item={item}
                isExpanded={expandedItems.includes(item.id)}
                onToggle={toggleExpanded}
                onNavigate={handleNavigate}
                hasRole={hasRole}
                collapsed={collapsed && !isMobile}
              />
            ))
          )}
        </List>
      </Box>

      <Dialog open={leaveDialogOpen} onClose={handleCancelLeave} maxWidth="xs" fullWidth>
        <DialogTitle>¿Salir de la empresa?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Está a punto de salir de <strong>{company?.name || 'esta empresa'}</strong> y volver al inicio
            global. ¿Desea continuar?
          </DialogContentText>
          <FormControlLabel
            sx={{ mt: 1.5 }}
            control={
              <Checkbox
                checked={skipLeaveConfirm}
                onChange={(e) => setSkipLeaveConfirm(e.target.checked)}
                size="small"
              />
            }
            label="No volver a mostrar este mensaje"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLeave}>No, regresar</Button>
          <Button onClick={handleConfirmLeave} variant="contained" color="primary">
            Sí, salir
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );

  // ============================================================
  // MOBILE LAYOUT: Top App Bar + Full-Screen Drawer
  // ============================================================
  if (isMobile) {
    return (
      <>
        {/* Top App Bar */}
        <AppBar
          position="fixed"
          sx={{
            height: TOP_APP_BAR_HEIGHT,
            zIndex: theme.zIndex.drawer + 1,
            bgcolor: (theme) => theme.palette.background.sidebar,
            color: 'inherit',
            boxShadow: 'none'
          }}
        >
          <Toolbar>
            <Box
              component="a"
              href="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexGrow: 1,
                textDecoration: 'none'
              }}
            >
              <img
                src={mainLogoUrl}
                alt={mainLogoUrl === '/safety-logo.png' ? "Safety Logo" : "Company Logo"}
                style={{
                  height: '40px',
                  width: 'auto',
                  maxWidth: '150px',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/safety-logo.png';
                }}
              />
            </Box>
            {/* Report button — mobile. Opens a menu with the same three
                options as the desktop header (Accidente / Llamado de
                Atención gated by report permission; Ticket de Soporte
                always available). */}
            {(onOpenIncident || onOpenSupportTicket) && (
              <>
                <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28 }}>
                  <Box sx={{
                    position: 'absolute',
                    inset: 0,
                    borderRadius: '50%',
                    pointerEvents: 'none',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      border: '1.5px solid',
                      borderColor: 'error.light',
                      opacity: 0,
                      '@keyframes ripple': {
                        '0%': { transform: 'scale(1)', opacity: 0.4 },
                        '100%': { transform: 'scale(1.8)', opacity: 0 },
                      },
                      animation: 'ripple 3s ease-out infinite',
                    },
                  }} />
                  <IconButton
                    aria-label="Reportar"
                    onClick={(e) => setReportMenuAnchor(e.currentTarget)}
                    size="small"
                    sx={{
                      color: 'error.light',
                      width: 28,
                      height: 28,
                      zIndex: 1,
                      '&:hover': {
                        bgcolor: 'error.main',
                        color: 'background.buttonText',
                      },
                    }}
                  >
                    <IncidentIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Box>
                <Menu
                  anchorEl={reportMenuAnchor}
                  open={Boolean(reportMenuAnchor)}
                  onClose={() => setReportMenuAnchor(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {onOpenIncident && (
                    <MuiMenuItem onClick={() => { setReportMenuAnchor(null); setMobileMenuOpen(false); onOpenIncident(); }}>
                      <ListItemIcon><IncidentIcon fontSize="small" color="error" /></ListItemIcon>
                      <ListItemText>Reportar Accidente</ListItemText>
                    </MuiMenuItem>
                  )}
                  {onOpenAttentionCall && (
                    <MuiMenuItem onClick={() => { setReportMenuAnchor(null); setMobileMenuOpen(false); onOpenAttentionCall(); }}>
                      <ListItemIcon><AttentionIcon fontSize="small" color="warning" /></ListItemIcon>
                      <ListItemText>Llamado de Atención</ListItemText>
                    </MuiMenuItem>
                  )}
                  {onOpenSupportTicket && (
                    <MuiMenuItem onClick={() => { setReportMenuAnchor(null); setMobileMenuOpen(false); onOpenSupportTicket(); }}>
                      <ListItemIcon><HelpIcon fontSize="small" color="info" /></ListItemIcon>
                      <ListItemText>Ticket de Soporte</ListItemText>
                    </MuiMenuItem>
                  )}
                </Menu>
              </>
            )}
            {/* Profile avatar */}
            {user && onOpenProfile && (
              <IconButton
                onClick={() => {
                  setMobileMenuOpen(false);
                  onOpenProfile?.();
                }}
                sx={{ mr: 0.5 }}
              >
                {userAvatarUrl ? (
                  <Box
                    component="img"
                    src={userAvatarUrl}
                    alt={user.name}
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <Box sx={{
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    color: '#fff',
                  }}>
                    {(user.name || '??').split(' ').map((w: string) => w.charAt(0)).join('').toUpperCase().slice(0, 2)}
                  </Box>
                )}
              </IconButton>
            )}
            <IconButton
              color="inherit"
              aria-label={mobileMenuOpen ? 'close menu' : 'open menu'}
              onClick={() => {
                const opening = !mobileMenuOpen;
                setMobileMenuOpen(opening);
                if (opening && profileOpen) {
                  onCloseProfile?.();
                }
              }}
              sx={{
                // AppBar above paints `background.sidebar` (dark). The
                // previous `text.primary` read as near-black on dark
                // and effectively disappeared. Match the rest of the
                // toolbar's contrast tokens.
                color: 'background.buttonText',
              }}
            >
              {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          </Toolbar>
        </AppBar>

        {/* Full-Screen Mobile Menu Drawer - Only render when open */}
        {mobileMenuOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: TOP_APP_BAR_HEIGHT,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100vw',
              height: `calc(100vh - ${TOP_APP_BAR_HEIGHT}px)`,
              bgcolor: (theme) => theme.palette.background.sidebar,
              zIndex: theme.zIndex.drawer,
              overflow: 'auto',
            }}
          >
            {/* Mobile Content with close button at top */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}>
              {SidebarContent}
            </Box>
          </Box>
        )}

        {/* Spacer for Top App Bar */}
        <Box sx={{ height: TOP_APP_BAR_HEIGHT }} />
      </>
    );
  }

  // ============================================================
  // DESKTOP LAYOUT: Persistent Sidebar
  // ============================================================
  return (
    <Drawer
      variant="permanent"
      data-tour="sidebar"
      sx={{
        width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
        flexShrink: 0,
        zIndex: 150,
        '& .MuiDrawer-paper': {
          width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxSizing: 'border-box',
          height: '100%',
          overflow: 'visible',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 150,
          bgcolor: (theme) => theme.palette.background.sidebar,
          borderRight: 'none',
          borderRadius: 0,
          boxShadow: 'none',
        },
      }}
    >
      {SidebarContent}
    </Drawer>
  );
};

export default Sidebar;